'use client';

import classNames from 'classnames';
import { useFormState } from 'react-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { sendEmail } from '@/actions/sendMail';
import { Contact } from '@/types/sanity.types';
import { SubmitButton } from './SubmitButton';

type Form = NonNullable<Contact['contact_form']>;
type Response = NonNullable<Contact['response_messages']>;
type Translations = Omit<Form, '_type'> & Omit<Response, '_type'>;

export const ContactForm = ({ translations }: { translations: Translations }) => {
  const [state, formAction] = useFormState(sendEmail, {});

  return (
    <form action={formAction} className="px-6 pb-24 sm:pb-32 lg:px-8 lg:py-48">
      <div className={classNames('mx-auto max-w-xl text-gray-900 dark:text-white lg:mr-0 lg:max-w-lg', {})}>
        {state.status && (
          <div
            className={classNames('mb-10 flex leading-10', {
              'text-green-500': state.status === 'success',
              'text-red-500': state.status === 'error',
            })}
          >
            {state.status === 'success' && <CheckCircleIcon className="w-[40px] min-w-[40px]" />}
            {state.status === 'error' && <ExclamationCircleIcon className="w-[40px] min-w-[40px]" />}

            <p
              aria-live="polite"
              className="flex w-[calc(100%-40px)] items-center overflow-hidden text-ellipsis pl-3 text-sm empty:hidden"
            >
              {state.status === 'success' ? translations?.success : translations?.error} {state.responseMessage}
            </p>
          </div>
        )}
        <div
          className={classNames('grid grid-cols-1 gap-x-8 gap-y-6 transition-all sm:grid-cols-2', {
            'overflow-hidden': state.status === 'success',
            'max-h-0': state.status === 'success',
            'max-h-[1000px]': state.status !== 'success',
          })}
        >
          <div>
            <label htmlFor="first-name" className="block text-sm font-semibold leading-6 ">
              {translations?.first_name}
            </label>
            <div className="mt-2.5">
              <input
                required
                type="text"
                name="first_name"
                id="first-name"
                autoComplete="given-name"
                className="highlight-ignore block w-full rounded-md border-0 bg-white/5 px-3.5 py-2  shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:ring-white/10 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div>
            <label htmlFor="last-name" className="block text-sm font-semibold leading-6">
              {translations?.last_name}
            </label>
            <div className="mt-2.5">
              <input
                type="text"
                name="last_name"
                id="last-name"
                autoComplete="family-name"
                className="highlight-ignore block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:ring-white/10 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="email" className="block text-sm font-semibold leading-6">
              {translations?.email}
            </label>
            <div className="mt-2.5">
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                className="highlight-ignore block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:ring-white/10 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="phone-number" className="block text-sm font-semibold leading-6">
              {translations?.phone}
            </label>
            <div className="mt-2.5">
              <input
                type="tel"
                name="phone_number"
                id="phone-number"
                autoComplete="tel"
                className="highlight-ignore block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:ring-white/10 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="message" className="block text-sm font-semibold leading-6">
              {translations?.message}
            </label>
            <div className="mt-2.5">
              <textarea
                name="message"
                id="message"
                rows={4}
                className="highlight-ignore block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:ring-white/10 sm:text-sm sm:leading-6"
                required
              />
            </div>
          </div>
        </div>
        <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="company-website">Company website</label>
          <input type="text" name="company_website" id="company-website" tabIndex={-1} autoComplete="off" />
        </div>
        {state.status !== 'success' && (
          <div className="mt-8 flex justify-end">
            <SubmitButton>{translations?.send_message}</SubmitButton>
          </div>
        )}
      </div>
    </form>
  );
};
